import * as RadixSelect from '@radix-ui/react-select';
import {
  forwardRef,
  useEffect,
  useRef,
  useState,
  type ComponentProps,
  type Dispatch,
  type PropsWithChildren,
  type SetStateAction,
} from 'react';
import { LegacyIconButton } from '~/components/ui/LegacyIconButton';
import { classNames } from '~/utils/classNames';
import { resetGlobalCursorStyle, setGlobalCursorStyle } from '~/utils/cursor-style';

interface Props extends PropsWithChildren {
  width: number;
  setWidth: Dispatch<SetStateAction<number>>;
  enabled: boolean;
  isPreviewFullscreen: boolean;
}

type Device = typeof deviceToSize extends Map<unknown, infer Value> ? Value : never;

// viewports from https://viewport-tester.com
const deviceToSize = new Map([
  ['iphone-16', { id: 'iphone-16', name: 'iPhone 16', width: 393, height: 852 }],
  ['pixel-9', { id: 'pixel-9', name: 'Pixel 9', width: 412, height: 915 }],
  ['galaxy-24', { id: 'galaxy-24', name: 'Galaxy 24', width: 360, height: 780 }],
]);

const devices = Array.from(deviceToSize.entries());

const DEFAULT_DEVICE = deviceToSize.get('iphone-16')!;

export default function Resizer({ enabled, width, setWidth, isPreviewFullscreen, children }: Props) {
  const parentRef = useRef<HTMLDivElement>(null);
  const draggableDivLeft = useRef<HTMLDivElement>(null);
  const draggableDivRight = useRef<HTMLDivElement>(null);

  const [isResizing, setIsResizing] = useState(false);
  const [zoom, setZoom] = useState(100);

  const [selectedDevice, setSelectedDevice] = useState<Device | undefined>(DEFAULT_DEVICE);

  useEffect(() => {
    if (!draggableDivLeft.current || !draggableDivRight.current || !parentRef.current) {
      return undefined;
    }

    const minWidth = 160;
    const parentContainer = parentRef.current;

    let parentContainerWidth = parentContainer.getBoundingClientRect().width;
    let maxWidth = parentContainerWidth;
    let initialMouseClientX = 0;
    let initialWidthValue = 0;
    let direction = 1;
    let timeoutId: ReturnType<typeof setTimeout>;

    function onMouseMove(event: MouseEvent) {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(resetState, 1000);

      // we multiply by 2 because we increase the size of the preview on both the right and left side
      const delta = 2 * (event.clientX - initialMouseClientX);

      // we apply the direction, each side will apply the delta in a different direction
      let newMobileWidth = initialWidthValue + direction * delta;

      newMobileWidth = Math.max(Math.min(newMobileWidth, maxWidth), minWidth);

      setWidth(newMobileWidth);
    }

    function onMouseUp() {
      resetState();
    }

    function resetState() {
      document.removeEventListener('mouseup', onMouseUp);
      document.removeEventListener('mousemove', onMouseMove);

      setIsResizing(false);
      resetGlobalCursorStyle();
    }

    function onMouseDown(this: HTMLDivElement, event: MouseEvent) {
      const draggableWidth = this.getBoundingClientRect().width;

      parentContainerWidth = parentContainer.getBoundingClientRect().width;
      initialMouseClientX = event.clientX;
      direction = this === draggableDivLeft.current ? -1 : 1;
      maxWidth = parentContainerWidth - 2 * draggableWidth;

      setWidth((mobileWidth) => {
        initialWidthValue = mobileWidth;

        document.addEventListener('mouseup', onMouseUp);
        document.addEventListener('mousemove', onMouseMove);

        return mobileWidth;
      });

      setGlobalCursorStyle('ew-resize');

      setIsResizing(true);
    }

    draggableDivRight.current.addEventListener('mousedown', onMouseDown);
    draggableDivLeft.current.addEventListener('mousedown', onMouseDown);

    return () => {
      draggableDivRight.current?.removeEventListener('mousedown', onMouseDown);
      draggableDivLeft.current?.removeEventListener('mousedown', onMouseDown);
    };
  }, [draggableDivLeft.current, draggableDivRight.current, parentRef.current, isPreviewFullscreen]);

  useEffect(() => {
    if (!selectedDevice) {
      setZoom(100);

      return;
    }

    const parentHeight = parentRef.current?.getBoundingClientRect().height;

    let zoomFactor = 1;

    // if selected device doesn't fit the preview, zoom out in steps of 10%
    if (parentHeight && selectedDevice.height > parentHeight) {
      const overflowPercentage = Math.round((100 * (selectedDevice.height - parentHeight)) / selectedDevice.height);
      zoomFactor = 1 - Math.ceil(overflowPercentage / 10) / 10;
    }

    setZoom(Math.round(100 * zoomFactor));
  }, [selectedDevice, enabled]);

  function onDeviceSelect(id: string) {
    setSelectedDevice(deviceToSize.get(id));
  }

  return (
    <div className="flex flex-col size-full max-h-100%">
      {enabled && (
        <div className="flex py-2.5">
          <div className="mx-auto flex">
            <DeviceSelect value={selectedDevice?.id || 'responsive'} onValueChange={onDeviceSelect} />

            {selectedDevice && <Zoom value={zoom} onChange={setZoom} />}
          </div>
        </div>
      )}

      <div ref={parentRef} className="flex items-center size-full overflow-auto">
        <DraggableHandle
          ref={draggableDivLeft}
          className={classNames('ml-auto', { hidden: !enabled || selectedDevice != null })}
        />

        <div
          className={classNames('size-full', {
            'select-none': isResizing,
            'mx-auto': selectedDevice != null,
          })}
          style={{
            width: enabled ? `${selectedDevice?.width || width}px` : undefined,
            height: enabled && selectedDevice ? `${selectedDevice.height}px` : undefined,

            // CSS zoom does not work in Firefox and Safari, use scale instead
            transform: enabled && zoom !== 100 ? `scale(${zoom / 100})` : undefined,
            maxHeight: enabled && zoom !== 100 ? '100%' : undefined,
          }}
        >
          {children}

          {isResizing && <div className="bg-transparent inset-0 absolute z-10" />}
        </div>

        <DraggableHandle
          ref={draggableDivRight}
          className={classNames('mr-auto', { hidden: !enabled || selectedDevice != null })}
        />
      </div>
    </div>
  );
}

const DraggableHandle = forwardRef<HTMLDivElement, { className: string }>(({ className }, ref) => {
  return (
    <div
      ref={ref}
      className={classNames(
        'h-full w-3 flex justify-center items-center hover:bg-bolt-elements-background-depth-3 cursor-ew-resize',
        className,
      )}
    >
      <div className="i-ph:dots-six-vertical-light" />
    </div>
  );
});

function DeviceSelect(props: ComponentProps<typeof RadixSelect.Root>) {
  return (
    <RadixSelect.Root {...props}>
      <RadixSelect.Trigger className="flex items-center gap-2 pl-3 pr-2 py-1 rounded-lg bg-bolt-elements-background-depth-4 text-bolt-elements-textSecondary focus:outline-none text-sm">
        <RadixSelect.Value />
        <RadixSelect.Icon asChild>
          <span className="i-ph:caret-down inline-block" />
        </RadixSelect.Icon>
      </RadixSelect.Trigger>

      <RadixSelect.Portal>
        <RadixSelect.Content
          position="popper"
          className="overflow-hidden bg-bolt-elements-background-depth-2 border border-bolt-elements-borderColor rounded-lg shadow z-dialog"
          sideOffset={3}
        >
          <RadixSelect.Viewport>
            <DeviceSelectItem value="responsive">Responsive</DeviceSelectItem>

            {devices.map(([id, device]) => (
              <DeviceSelectItem key={id} value={id}>
                {device.name}
              </DeviceSelectItem>
            ))}
          </RadixSelect.Viewport>
          <RadixSelect.Arrow className="fill-bolt-elements-borderColor" />
        </RadixSelect.Content>
      </RadixSelect.Portal>
    </RadixSelect.Root>
  );
}

function DeviceSelectItem({ children, ...props }: ComponentProps<typeof RadixSelect.Item>) {
  return (
    <RadixSelect.Item
      {...props}
      className="relative flex items-center px-8 py-2 text-sm cursor-pointer hover:bg-bolt-elements-item-backgroundActive focus:outline-none"
    >
      <RadixSelect.ItemText>
        <span>{children}</span>
      </RadixSelect.ItemText>

      <RadixSelect.ItemIndicator className="absolute left-2 inline-flex items-center">
        <span className="i-ph:check" />
      </RadixSelect.ItemIndicator>
    </RadixSelect.Item>
  );
}

function Zoom({ value, onChange }: { value: number; onChange: (level: number) => void }) {
  return (
    <div className="flex items-center gap-2 ml-4 px-2 py-0.75 rounded-lg bg-bolt-elements-background-depth-4 text-bolt-elements-textSecondary text-sm">
      <LegacyIconButton
        title="Zoom out"
        size="lg"
        disabled={50 >= value}
        icon="i-ph:magnifying-glass-minus"
        onClick={() => onChange(value - 10)}
      />

      <span>{value} %</span>

      <LegacyIconButton
        title="Zoom in"
        disabled={value >= 150}
        size="lg"
        icon="i-ph:magnifying-glass-plus"
        onClick={() => onChange(value + 10)}
      />
    </div>
  );
}
